import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { useGetEmployeeByIdQuery } from "../../../common/api/employee";
import { useGetCertificatesQuery } from "../../../common/api/certificate";
import { useGetQualificationsQuery } from "../../../common/api/qualification";
import { useEstablishmentId } from "../../../common/utils/useEstablishmentId";
import { useIsEstablishmentManager } from "../../../common/utils/useIsEstablishmentManager";
import { QualificationTypeEnum } from "../../../common/enums/QualificationTypeEnum";
import editImage from "../../../common/images/edit.svg";
import ellipsisWhiteImage from "../../../common/images/ellipsis-white.svg";
import { getInfoCardData } from "../utils/getInfoCardData";
import { getEmployeeDetailItems } from "../utils/getEmployeeDetailItems";
import {
    setIsEditEmployeeModalOpen,
    setSelectedEmployee,
} from "../../../modals/EditEmployeeModal/EditEmployeeModalSlice";
import {
    setIsLeftTagModalOpenIndex,
    setIsRightTagModalOpenIndex,
} from "../EmployeeDetailSlice";

type UseEmployeeDetailMainResult = {
    isInitialLoading: boolean;
    isSecondaryFetching: boolean;
    items: ReturnType<typeof getEmployeeDetailItems>;
    infoCardData: ReturnType<typeof getInfoCardData>;
    widgetHeader: {
        title: string;
        button: {
            text: string;
            iconUrl: string;
            onClick: () => void;
        };
    };
};

export const useEmployeeDetailMain = (): UseEmployeeDetailMainResult | null => {
    const EmployeeId = Number(useParams().employeeId);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const establishmentId = useEstablishmentId();

    const {
        certificateList,
        isLeftTagModalOpenIndex,
        isRightTagModalOpenIndex,
        section,
    } = useAppSelector((state) => state.employeeDetail);

    const modalRef = useRef<HTMLDivElement | null>(null);
    const [openModalIndex, setOpenModalIndex] = useState(-1);
    const isEstablishmentManager = useIsEstablishmentManager() ?? false;

    const { isPostCreateCertificateSuccess } = useAppSelector(
        (state) => state.createCertificateModal,
    );
    const { isPatchEditEmployeeSuccess } = useAppSelector(
        (state) => state.editEmployeeModal,
    );

    const handleClickOutside = useCallback(
        (event: MouseEvent): void => {
            if (
                modalRef.current &&
                !modalRef.current.contains(event.target as Node)
            ) {
                setOpenModalIndex(-1);
                dispatch(setIsLeftTagModalOpenIndex(-1));
                dispatch(setIsRightTagModalOpenIndex(-1));
            }
        },
        [dispatch],
    );

    useEffect(() => {
        if (openModalIndex !== null) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [openModalIndex, handleClickOutside]);

    const {
        data: employeeData,
        isLoading: employeeDataIsLoading,
        isFetching: employeeDataIsFetching,
        refetch: refetchEmployeeData,
    } = useGetEmployeeByIdQuery({ id: EmployeeId });

    const {
        data: certificates,
        isLoading: certificateDataIsLoading,
        isFetching: certificateDataIsFetching,
        refetch: refetchCertificates,
    } = useGetCertificatesQuery({
        items_per_page: 5,
        page: certificateList.currentPage,
        filter: {
            status: certificateList.activeTab,
            employee_id: EmployeeId,
            establishment_id: establishmentId,
        },
    });

    const {
        data: qualificationData,
        isLoading: qualificationDataIsLoading,
        isFetching: qualificationDataIsFetching,
        refetch: refetchQualificationData,
    } = useGetQualificationsQuery(
        {
            items_per_page: 5,
            page: certificateList.currentPage,
            filter: {
                interesting_for_employee: EmployeeId,
                type: QualificationTypeEnum.PERSON,
                function_group_id:
                    employeeData?.function_groups
                        ?.map((functionGroup) => functionGroup.id)
                        .join(",") ?? "",
            },
        },
        { skip: !employeeData },
    );

    useEffect(() => {
        if (isPostCreateCertificateSuccess) {
            refetchCertificates();
            refetchEmployeeData();
            refetchQualificationData();
        }
    }, [
        isPostCreateCertificateSuccess,
        refetchQualificationData,
        refetchCertificates,
        refetchEmployeeData,
    ]);

    useEffect(() => {
        if (isPatchEditEmployeeSuccess) {
            refetchEmployeeData();
        }
    }, [isPatchEditEmployeeSuccess, refetchEmployeeData]);

    const isInitialLoading = useMemo(
        () =>
            employeeDataIsLoading ||
            certificateDataIsLoading ||
            qualificationDataIsLoading ||
            employeeDataIsFetching ||
            certificateDataIsFetching ||
            qualificationDataIsFetching,
        [
            employeeDataIsLoading,
            certificateDataIsLoading,
            qualificationDataIsLoading,
            employeeDataIsFetching,
            certificateDataIsFetching,
            qualificationDataIsFetching,
        ],
    );

    const widgetHeader = useMemo(
        () => ({
            title: "Persoonlijke gegevens",
            button: {
                text: "bewerken",
                iconUrl: editImage,
                onClick: () => {
                    if (!employeeData) return;
                    dispatch(setIsEditEmployeeModalOpen(true));
                    dispatch(setSelectedEmployee(employeeData));
                },
            },
        }),
        [dispatch, employeeData],
    );

    if (!employeeData) {
        return {
            isInitialLoading,
            isSecondaryFetching: Boolean(
                certificateDataIsFetching || qualificationDataIsFetching,
            ),
            items: [],
            infoCardData: undefined,
            widgetHeader,
        };
    }

    const items = getEmployeeDetailItems(employeeData);

    const infoCardData = getInfoCardData({
        certificateDataIsFetching,
        isEstablishmentManager,
        certificates,
        employeeData,
        dispatch,
        navigate,
        openModalIndex,
        setOpenModalIndex,
        modalRef,
        ellipsisWhiteImage,
        certificateList,
        isRightTagModalOpenIndex,
        isLeftTagModalOpenIndex,
        qualificationData,
        qualificationDataIsFetching,
        section,
    });

    return {
        isInitialLoading,
        isSecondaryFetching: Boolean(
            certificateDataIsFetching || qualificationDataIsFetching,
        ),
        items,
        infoCardData,
        widgetHeader,
    };
};
